import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, applyPagedSelect, totalPagesFromCount } from "../lib/adminPagination";
import {
  adminCard,
  adminEmpty,
  adminPageBg,
  adminPageTitle,
  adminPrimaryBtn,
  adminRow,
  adminSearchInput,
  adminSearchWrap,
  adminStatusPill,
  adminTabActive,
  adminTabBase,
  adminTabIdle,
  adminTableHead,
  adminTableWrap,
  adminTd,
  adminTdName,
} from "./adminUi";
import { deriveAdminUiStatus } from "../lib/proposalContract";

const PAGE_SIZE = ADMIN_PAGE_SIZE;

const statusLabel = (value) => {
  if (value === "in_review") return "In Review";
  if (value === "initial_consultation") return "Initial Consultation";
  if (value === "proposal_contract" || value === "proposal_quotation" || value === "agreement_confirmation" || value === "contract_signing") {
    return "Proposal & Contract";
  }
  if (value === "event_preparation") return "Event Preparation";
  if (value === "event_live") return "Posted on Live Event";
  if (value === "accepted" || value === "approved") return "Event Preparation";
  if (value === "declined") return "Declined";
  return String(value || "in_review").charAt(0).toUpperCase() + String(value || "in_review").slice(1);
};

const deriveUiStatus = (booking) => deriveAdminUiStatus(booking);

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric", year: "numeric" });
};

const Bookings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("concert");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tabCounts, setTabCounts] = useState({ concert: 0, corporate: 0, archive: 0 });
  const listTopRef = useRef(null);
  const [bookings, setBookings] = useState([]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString();

    await supabase
      .from("bookings")
      .update({ status: "in_review" })
      .eq("status", "new")
      .lt("created_at", twelveHoursAgo);

    const [concertCount, corporateCount, archiveCount] = await Promise.all([
      supabase.from("bookings").select("id", { count: "exact", head: true }).neq("status", "declined").neq("booking_type", "corporate"),
      supabase.from("bookings").select("id", { count: "exact", head: true }).neq("status", "declined").eq("booking_type", "corporate"),
      supabase.from("bookings").select("id", { count: "exact", head: true }).eq("status", "declined"),
    ]);
    setTabCounts({
      concert: Number(concertCount.count) || 0,
      corporate: Number(corporateCount.count) || 0,
      archive: Number(archiveCount.count) || 0,
    });

    let query = supabase
      .from("bookings")
      .select("*", { count: "exact" })
      .order("updated_at", { ascending: false });

    if (activeTab === "archive") {
      query = query.eq("status", "declined");
    } else {
      query = query.neq("status", "declined");
      query = activeTab === "corporate"
        ? query.eq("booking_type", "corporate")
        : query.neq("booking_type", "corporate");
      if (statusFilter !== "all") query = query.eq("status", statusFilter);
    }

    const needle = search.trim();
    if (needle) {
      const like = `%${needle}%`;
      query = query.or(
        `requester_name.ilike.${like},requester_email.ilike.${like},request_code.ilike.${like},event_name.ilike.${like},theme.ilike.${like},venue.ilike.${like}`
      );
    }

    const { data, error, count } = await applyPagedSelect(query, currentPage, PAGE_SIZE);

    if (error) {
      console.error("Failed to load bookings:", error);
      setBookings([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    setBookings((data || []).map((row) => ({
      ...row,
      status: deriveUiStatus(row),
    })));
    setTotalCount(Number(count) || (data || []).length);
    setLoading(false);
  }, [activeTab, currentPage, search, statusFilter]);

  useEffect(() => {
    fetchBookings();

    const channel = supabase
      .channel("admin-bookings-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, fetchBookings)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchBookings]);

  const filteredBookings = bookings;
  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const paginatedBookings = filteredBookings;
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeTab, statusFilter]);

  const isArchiveTab = activeTab === "archive";
  const columnWidthClass = isArchiveTab || activeTab === "corporate" ? "w-1/6" : "w-1/5";
  const tableColSpan = isArchiveTab || activeTab === "corporate" ? 6 : 5;

  return (
    <div className={adminPageBg}>
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
            <h1 className={adminPageTitle}>Bookings</h1>

            <div className={adminSearchWrap}>
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search..."
                className={adminSearchInput}
              />
            </div>
          </div>

          <div className={adminCard} ref={listTopRef}>

          <div className="flex flex-wrap gap-4 sm:gap-6 border-b border-gray-200 mb-4">
            {[
              { key: "concert", label: `Concerts (${tabCounts.concert})` },
              { key: "corporate", label: `Corporate Events (${tabCounts.corporate})` },
              { key: "archive", label: `Archive (${tabCounts.archive})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setStatusFilter("all");
                  setCurrentPage(1);
                }}
                className={`${adminTabBase} ${
                  activeTab === tab.key ? adminTabActive : adminTabIdle
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <BusyTable show={showSkeleton} tone="admin">
          <div className={adminTableWrap}>
            <table className={`w-full table-fixed border-collapse ${isArchiveTab || activeTab === "corporate" ? "min-w-[900px]" : "min-w-[760px]"}`}>
              <thead>
                <tr className={adminTableHead}>
                  <th className={`px-4 py-3 text-left ${columnWidthClass}`}>Client Name</th>
                  {isArchiveTab ? <th className={`px-4 py-3 text-left ${columnWidthClass}`}>Type</th> : null}
                  {activeTab === "concert" || isArchiveTab ? (
                    <th className={`px-4 py-3 text-left ${columnWidthClass}`}>Event Name</th>
                  ) : (
                    <th className={`px-4 py-3 text-left ${columnWidthClass}`}>Target Date</th>
                  )}
                  <th className={`px-4 py-3 text-left ${columnWidthClass}`}>Budget</th>
                  {activeTab === "corporate" ? <th className={`px-4 py-3 text-left ${columnWidthClass}`}>Venue</th> : null}
                  {isArchiveTab ? (
                    <th className={`px-4 py-3 text-left ${columnWidthClass}`}>Reason</th>
                  ) : (
                    <th className={`px-4 py-3 text-left ${columnWidthClass}`}>
                      <div className="inline-flex items-center gap-2">
                        <span>Status</span>
                        <select
                          value={statusFilter}
                          onChange={(event) => {
                            setStatusFilter(event.target.value);
                            setCurrentPage(1);
                          }}
                          className="h-6 rounded border border-gray-500 bg-black px-1 text-[11px] text-white focus:outline-none"
                        >
                          <option value="all">All</option>
                          <option value="in_review">In Review</option>
                          <option value="initial_consultation">Initial Consultation</option>
                          <option value="proposal_contract">Proposal & Contract</option>
                          <option value="event_preparation">Event Preparation</option>
                          <option value="event_live">Posted on Live Event</option>
                        </select>
                      </div>
                    </th>
                  )}
                  <th className={`px-4 py-3 text-center ${columnWidthClass}`}>View</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBookings.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className={adminEmpty}>
                      {loading
                        ? "Loading..."
                        : isArchiveTab
                        ? (search ? "No archived bookings match this search." : "No archived bookings yet.")
                        : (search || statusFilter !== "all" ? "No bookings match this search." : "No bookings yet.")}
                    </td>
                  </tr>
                ) : (
                  paginatedBookings.map((booking) => (
                    <tr key={booking.id} className={adminRow}>
                      <td className={`${adminTdName} ${columnWidthClass}`}>
                        <div>{booking.requester_name || "Unknown"}</div>
                        <div className="text-xs font-normal text-gray-500">{booking.request_code || `Booking #${booking.id}`}</div>
                      </td>
                      {isArchiveTab ? (
                        <td className={`${adminTd} ${columnWidthClass}`}>
                          {booking.booking_type === "corporate" ? "Corporate" : "Concert"}
                        </td>
                      ) : null}
                      {activeTab === "concert" || isArchiveTab ? (
                        <td className={`${adminTd} ${columnWidthClass}`}>{booking.event_name || "Untitled Event"}</td>
                      ) : (
                        <td className={`${adminTd} ${columnWidthClass}`}>{formatDisplayDate(booking.event_date || booking.created_at)}</td>
                      )}
                      <td className={`${adminTd} ${columnWidthClass}`}>{booking.budget_range || "-"}</td>
                      {activeTab === "corporate" ? <td className={`${adminTd} ${columnWidthClass}`}>{booking.venue || "-"}</td> : null}
                      {isArchiveTab ? (
                        <td className={`${adminTd} ${columnWidthClass}`}>
                          <p className="line-clamp-3">{booking.decline_reason || "-"}</p>
                        </td>
                      ) : (
                        <td className={`px-4 py-4 ${columnWidthClass}`}>
                          <span className={`${adminStatusPill} ${booking.status === "event_preparation" || booking.status === "accepted" || booking.status === "approved" ? "bg-fuchsia-100 text-fuchsia-700" : booking.status === "event_live" ? "bg-green-100 text-green-700" : booking.status === "declined" ? "bg-red-100 text-red-700" : booking.status === "initial_consultation" ? "bg-blue-100 text-blue-700" : booking.status === "proposal_contract" ? "bg-purple-100 text-purple-700" : booking.status === "in_review" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700"}`}>
                            {statusLabel(booking.status)}
                          </span>
                        </td>
                      )}
                      <td className={`px-4 py-4 text-center ${columnWidthClass}`}>
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/bookings/${booking.id}`)}
                          className={adminPrimaryBtn}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </BusyTable>

          <AdminPagination
            className="mt-6"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
            </div>
        </div>
      </div>

    </div>
  );
};

export default Bookings;
